import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { eur } from "@/lib/format";
import { ArrowLeft, MapPin, Users, BedDouble, Bath, Check, Loader2, Eye } from "lucide-react";

export default function PropertyPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [prop, setProp] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/schema/property"), api.get(`/properties/${id}`)])
      .then(([s, p]) => { setSchema(s.data); setProp(p.data); }).catch(() => {});
  }, [id]);

  if (!prop || !schema) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const d = prop.data || {};
  const photos = prop.photos || [];
  const cover = photos.find((p) => p.is_cover) || photos[0];
  const name = d.name || "Immobile";
  const location = [d.address, d.cap, d.city, d.province].filter(Boolean).join(", ");
  const guests = d.max_guests || d.guests;
  const amenityFields = [];
  const textSections = [];
  (schema.sections || []).forEach((sec) => {
    (sec.fields || []).forEach((f) => {
      if (f.type === "gallery") return;
      const v = d[f.key];
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return;
      if (f.type === "amenities") amenityFields.push({ f, v });
      else if (f.key === "description_html" || f.type === "richtext") textSections.unshift({ f, v });
      else textSections.push({ f, v });
    });
  });
  const amenities = amenityFields.flatMap((a) => a.v);
  const rules = [...(prop.standard_rules_enabled || []), ...(prop.custom_rules || []).map((r) => (typeof r === "string" ? r : r.label))].filter(Boolean);

  return (
    <div className="-mx-4 md:-mx-6 -my-5 md:-my-7 min-h-[calc(100vh-7rem)] bg-white">
      <div className="sticky top-0 z-10 bg-neutral-900 text-white px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate(`/properties/${id}`)} className="flex items-center gap-2 text-sm font-medium" data-testid="preview-back-button"><ArrowLeft className="h-4 w-4" /> Torna all'editor</button>
        <span className="text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: "#E3FF00", color: "#111" }}><Eye className="h-3.5 w-3.5" /> Anteprima cliente</span>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="rounded-2xl overflow-hidden bg-muted aspect-[16/9] mb-4">
          {cover ? <img src={fileUrl(cover.url)} alt={name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-muted-foreground">Nessuna foto</div>}
        </div>
        {photos.length > 1 && (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-6">
            {photos.slice(0, 12).map((p) => <img key={p.id} src={fileUrl(p.url)} alt="" className="aspect-square object-cover rounded-lg" />)}
          </div>
        )}

        <h1 className="font-display text-3xl font-bold tracking-tight text-neutral-900">{name}</h1>
        {location && <p className="text-neutral-500 flex items-center gap-1.5 mt-1"><MapPin className="h-4 w-4" /> {location}</p>}

        <div className="flex flex-wrap gap-5 mt-4 text-sm text-neutral-700">
          {d.property_type && <span className="font-medium">{d.property_type}</span>}
          {guests && <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-neutral-400" /> {guests} ospiti</span>}
          {d.bedrooms != null && <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-neutral-400" /> {d.bedrooms} camere</span>}
          {d.bathrooms != null && <span className="flex items-center gap-1.5"><Bath className="h-4 w-4 text-neutral-400" /> {d.bathrooms} bagni</span>}
        </div>

        {textSections.filter((s) => s.f.key === "description_html").map((s) => (
          <p key={s.f.key} className="mt-6 text-neutral-700 leading-relaxed whitespace-pre-wrap">{String(s.v).replace(/<[^>]+>/g, "")}</p>
        ))}

        {amenities.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-neutral-900 mb-3">Servizi & Dotazioni</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {amenities.map((a) => <span key={a} className="flex items-center gap-2 text-sm text-neutral-700"><Check className="h-4 w-4 text-emerald-600" /> {a}</span>)}
            </div>
          </div>
        )}

        <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-2">
          {textSections.filter((s) => s.f.key !== "description_html" && s.f.type !== "amenities").map((s) => {
            const isCost = /cost|fee|tax|deposit|cauzione|pulizia|biancheria/i.test(s.f.key);
            const val = isCost && !isNaN(Number(s.v)) ? eur(Number(s.v) * 100) : String(s.v);
            return (
              <div key={s.f.key} className="flex items-center justify-between py-1.5 border-b border-neutral-100 text-sm">
                <span className="text-neutral-500">{s.f.label}</span>
                <span className="font-medium text-neutral-800">{val}</span>
              </div>
            );
          })}
        </div>

        {rules.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-neutral-900 mb-3">Regole della casa</h2>
            <ul className="space-y-1.5">
              {rules.map((r, i) => <li key={i} className="text-sm text-neutral-700 flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-neutral-400 shrink-0" /> {r}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
